import { useState } from 'react';
import { useAppDispatch } from 'app/config/store';
import { getMatricules } from '../../eleve/eleve.reducer';
import { PayloadAction } from '@reduxjs/toolkit';
import { UploadType } from '../upload.types';

export const useValidation = (ecoleId: string) => {
  const [errors, setErrors] = useState([]);
  const [errorMap, setErrorMap] = useState({});
  const dispatch = useAppDispatch();

  const validateData = (data: any[], headers: string[], selectedOperation: any) => {
    const newErrors = [];
    const newErrorMap = {};
    const matriculesSet = new Set();

    const expectedHeaders = selectedOperation.schema;
    const actualHeaders = headers;

    const invalidHeaders = expectedHeaders.length !== actualHeaders.length;

    if (invalidHeaders) {
      newErrors.push({
        line: 1,
        errors: `Les en-têtes du fichier ne correspondent pas au modèle attendu. Attendu: ${expectedHeaders.join(
          ', '
        )}. Trouvé: ${actualHeaders.join(', ')}`,
        type: 'INVALID_HEADER',
        fields: actualHeaders,
      });

      setErrors(newErrors);
      setErrorMap(newErrorMap);
      return;
    }

    // Get all matricules for the ecole
    dispatch(getMatricules(ecoleId))
      .then((response: PayloadAction<string[], string, { arg: any; requestId: string; requestStatus: 'fulfilled' }, never>) => {
        const existingMatricules = new Set(response.payload);

        data.forEach((row, index) => {
          const lineNumber = index + 1;
          const matricule = row['Matricule']?.trim();
          const phoneNumber = row['Téléphone du Parent']?.toString()?.trim();
          const parentName = `${row['Nom du Parent']?.trim()} ${row['Prénom du Parent']?.trim()}`;

          const missingFields = selectedOperation.schema.filter(field => {
            if (field === 'Montant de Scolarité') {
              return row[field] === null || row[field] === undefined || row[field] === '';
            }
            if (field === "Montant d'inscription") {
              return false;
            }

            if (field === 'Nouveau Matricule') {
              return false;
            }
            return !row[field];
          });

          if (missingFields.length > 0) {
            newErrors.push({
              line: lineNumber,
              errors: `Champs manquants: ${missingFields.join(', ')} pour le parent ${parentName || 'inconnu'}`,
              type: 'MISSING_FIELD',
              fields: missingFields,
            });

            if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
            missingFields.forEach(field => {
              newErrorMap[lineNumber][field] = 'MISSING_FIELD';
            });
          }

          if (matricule) {
            if (matriculesSet.has(matricule)) {
              newErrors.push({
                line: lineNumber,
                errors: `Le matricule ${matricule} est dupliqué pour le parent ${parentName || 'inconnu'}`,
                type: 'DUPLICATE',
                fields: ['Matricule'],
              });

              if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
              newErrorMap[lineNumber]['Matricule'] = 'DUPLICATE';
            } else if (existingMatricules.has(matricule) && selectedOperation.label === UploadType.CREATION) {
              newErrors.push({
                line: lineNumber,
                errors: `Le matricule ${matricule} existe déjà dans l'école pour le parent ${parentName || 'inconnu'}`,
                type: 'EXISTING_MATRICULE',
                fields: ['Matricule'],
              });

              if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
              newErrorMap[lineNumber]['Matricule'] = 'EXISTING_MATRICULE';
            } else {
              matriculesSet.add(matricule);
            }
          }

          if (phoneNumber) {
            const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
            if (cleanedPhoneNumber.length < 9) {
              newErrors.push({
                line: lineNumber,
                errors: `Le numéro de téléphone du parent ${parentName || 'inconnu'} doit contenir au moins 9 chiffres`,
                type: 'PHONE',
                fields: ['Téléphone du Parent'],
              });

              if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
              newErrorMap[lineNumber]['Téléphone du Parent'] = 'PHONE';
            }
          }
        });

        setErrors(newErrors);
        setErrorMap(newErrorMap);
      })
      .catch(error => {
        console.error('Error fetching matricules:', error);
        // Continue with validation without checking existing matricules
        data.forEach((row, index) => {
          const lineNumber = index + 1;
          const matricule = row['Matricule']?.trim();
          const phoneNumber = row['Téléphone du Parent']?.toString()?.trim();
          const parentName = `${row['Nom du Parent']?.trim()} ${row['Prénom du Parent']?.trim()}`;

          const missingFields = selectedOperation.schema.filter(field => {
            if (field === 'Montant de Scolarité') {
              return row[field] === null || row[field] === undefined || row[field] === '';
            }
            if (field === "Montant d'inscription") {
              if (row[field] === null || row[field] === undefined || row[field] === '' || isNaN(parseFloat(row[field]))) {
                row[field] = 0;
              }
            }
            return !row[field];
          });

          if (missingFields.length > 0) {
            newErrors.push({
              line: lineNumber,
              errors: `Champs manquants: ${missingFields.join(', ')} pour le parent ${parentName || 'inconnu'}`,
              type: 'MISSING_FIELD',
              fields: missingFields,
            });

            if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
            missingFields.forEach(field => {
              newErrorMap[lineNumber][field] = 'MISSING_FIELD';
            });
          }

          if (matricule) {
            if (matriculesSet.has(matricule)) {
              newErrors.push({
                line: lineNumber,
                errors: `Le matricule ${matricule} est dupliqué pour le parent ${parentName || 'inconnu'}`,
                type: 'DUPLICATE',
                fields: ['Matricule'],
              });

              if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
              newErrorMap[lineNumber]['Matricule'] = 'DUPLICATE';
            } else {
              matriculesSet.add(matricule);
            }
          }

          if (phoneNumber) {
            const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
            if (cleanedPhoneNumber.length < 9) {
              newErrors.push({
                line: lineNumber,
                errors: `Le numéro de téléphone du parent ${parentName || 'inconnu'} doit contenir au moins 9 chiffres`,
                type: 'PHONE',
                fields: ['Téléphone du Parent'],
              });

              if (!newErrorMap[lineNumber]) newErrorMap[lineNumber] = {};
              newErrorMap[lineNumber]['Téléphone du Parent'] = 'PHONE';
            }
          }
        });

        setErrors(newErrors);
        setErrorMap(newErrorMap);
      });
  };

  return {
    errors,
    errorMap,
    validateData,
  };
};
